import React from 'react';
import { TableVirtuoso } from 'react-virtuoso';
import { Icon, Button, Tooltip, Checkbox, Modal, Dropdown, Menu, Input, Popconfirm } from 'antd';

var thBase = {
  padding: '10px 12px', background: '#fafafa',
  borderBottom: '1px solid #e8e8e8',
  borderRight: '1px solid #e8e8e8',
  fontWeight: 600, fontSize: 13, textAlign: 'left',
  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  color: '#595959'
};
var tdBase = {
  padding: '10px 12px', background: '#fff',
  borderBottom: '1px solid #f0f0f0',
  borderRight: '1px solid #f0f0f0',
  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  fontSize: 13
};

function thS(w, extra) { var s = Object.assign({}, thBase, extra); if (w) s.width = w; return s; }
function tdS(w, extra) { var s = Object.assign({}, tdBase, extra); if (w) s.width = w; return s; }

/**
 * Reusable virtualized table for settings pages.
 *
 * Props:
 * - data: array of items
 * - columns: array of { key, title, width (optional), render(record) }
 * - onEdit(record): called when edit clicked
 * - onDelete(record): called when delete clicked
 * - onRowClick(record): called when row clicked (optional)
 * - onBulkDelete(records): called for bulk delete (optional, enables checkboxes)
 * - deletingId: id of record currently being deleted (shows loading)
 * - actionsWidth: width of actions column (default 80)
 * - height: table height (default: calculated from window)
 */
var SettingsVirtualTable = React.memo(function SettingsVirtualTable(props) {
  var data = props.data || [];
  var columns = props.columns || [];
  var onEdit = props.onEdit;
  var onDelete = props.onDelete;
  var onRowClick = props.onRowClick || onEdit;
  var onBulkDelete = props.onBulkDelete || onDelete;
  var onInlineEdit = props.onInlineEdit; // function(record, newValue) - for inline text save
  var onInlineDelete = props.onInlineDelete || onDelete; // function(record) - for inline delete
  var onQuickAdd = props.onQuickAdd; // function(value) - for quick add input
  var addTrigger = props.addTrigger || 0;
  var addLabel = props.addLabel || 'Novo item';
  var inlineEdit = !!onInlineEdit;
  var deletingId = props.deletingId;
  var headerActions = props.headerActions;
  var checkWidth = 44;

  var addModalState = React.useState(false);
  var addModalVisible = addModalState[0];
  var setAddModalVisible = addModalState[1];
  var addInputState = React.useState('');
  var addInputValue = addInputState[0];
  var setAddInputValue = addInputState[1];

  // Open modal when + button is clicked
  var prevTriggerRef = React.useRef(addTrigger);
  React.useEffect(function() {
    if (addTrigger > 0 && addTrigger !== prevTriggerRef.current && onQuickAdd) {
      setAddInputValue('');
      setAddModalVisible(true);
    }
    prevTriggerRef.current = addTrigger;
  }, [addTrigger]);

  var handleModalAdd = function() {
    var val = addInputValue && addInputValue.trim();
    if (!val) return;
    onQuickAdd(val);
    setAddInputValue('');
    setAddModalVisible(false);
  };

  var editingState = React.useState(null);
  var editingId = editingState[0];
  var setEditingId = editingState[1];
  var editValueState = React.useState('');
  var editValue = editValueState[0];
  var setEditValue = editValueState[1];

  var selectedState = React.useState({});
  var selectedMap = selectedState[0];
  var setSelectedMap = selectedState[1];

  var filtersState = React.useState({});
  var activeFilters = filtersState[0];
  var setActiveFilters = filtersState[1];

  // Reset selection when data changes
  React.useEffect(function() {
    setSelectedMap({});
  }, [data.length]);

  var searchTerm = props.searchTerm || '';

  // Apply filters and search to data
  var filteredData = React.useMemo(function() {
    var result = data;

    // Column filters
    var filterKeys = Object.keys(activeFilters);
    if (filterKeys.length > 0) {
      result = result.filter(function(record) {
        return filterKeys.every(function(key) {
          var filterVal = activeFilters[key];
          if (filterVal === null || filterVal === undefined) return true;
          var col = columns.find(function(c) { return c.key === key; });
          if (col && col.filterValue) return String(col.filterValue(record)) === String(filterVal);
          return String(record[key]) === String(filterVal);
        });
      });
    }

    // Search
    if (searchTerm && searchTerm.trim()) {
      var term = searchTerm.toLowerCase().trim();
      result = result.filter(function(record) {
        return columns.some(function(col) {
          var val = col.render ? col.render(record) : record[col.key];
          return val && String(val).toLowerCase().indexOf(term) !== -1;
        });
      });
    }

    return result;
  }, [data, activeFilters, columns, searchTerm]);

  var dataRef = React.useRef({});
  dataRef.current.data = filteredData;
  dataRef.current.selectedMap = selectedMap;
  dataRef.current.editingId = editingId;

  var selectedIds = Object.keys(selectedMap).filter(function(k) { return selectedMap[k]; });
  var selectedCount = selectedIds.length;
  var allSelected = filteredData.length > 0 && selectedCount === filteredData.length;

  var toggleSelect = function(id, e) {
    if (e) e.stopPropagation();
    var next = Object.assign({}, selectedMap);
    next[id] = !next[id];
    setSelectedMap(next);
  };

  var toggleAll = function() {
    if (allSelected) {
      setSelectedMap({});
    } else {
      var next = {};
      filteredData.forEach(function(d) { next[d.id] = true; });
      setSelectedMap(next);
    }
  };

  var handleBulkDelete = function() {
    var items = filteredData.filter(function(d) { return selectedMap[d.id]; });
    if (items.length === 0) return;
    Modal.confirm({
      title: 'Excluir ' + items.length + ' item' + (items.length > 1 ? 's' : '') + '?',
      content: 'Esta ação não poderá ser desfeita.',
      okText: 'Excluir',
      okType: 'danger',
      cancelText: 'Cancelar',
      onOk: function() {
        items.forEach(function(item) { onBulkDelete(item); });
        setSelectedMap({});
      }
    });
  };

  var tableComponents = React.useMemo(function() {
    return {
      Table: function(p) {
        return React.createElement('table', Object.assign({}, p, {
          style: Object.assign({}, p.style, {
            width: '100%',
            tableLayout: 'fixed',
            borderCollapse: 'collapse'
          })
        }));
      },
      TableRow: function(p) {
        var d = dataRef.current;
        var record = d.data[p['data-item-index']];
        var isSelected = record && d.selectedMap[record.id];
        var isEditingRow = record && d.editingId === record.id;
        return React.createElement('tr', Object.assign({}, p, {
          className: 'row-clabs' + (isSelected ? ' row-selected' : '') + (p.className ? ' ' + p.className : ''),
          style: Object.assign({}, p.style, {
            cursor: inlineEdit ? 'default' : 'pointer',
            background: isSelected ? '#e6f7ff' : undefined
          }),
          onClick: function() {
            if (inlineEdit) return;
            if (record && onRowClick) onRowClick(record);
          }
        }));
      }
    };
  }, [onRowClick]);

  var fixedHeaderContent = React.useCallback(function() {
    var ths = [];
    // Checkbox header
    ths.push(
      React.createElement('th', {
        key: '_check',
        style: thS(checkWidth, { textAlign: 'center', position: 'sticky', left: 0, zIndex: 3, overflow: 'visible' })
      },
        React.createElement(Checkbox, {
          checked: allSelected,
          indeterminate: selectedCount > 0 && !allSelected,
          onChange: toggleAll
        })
      )
    );
    columns.forEach(function(col) {
      var filterActive = activeFilters[col.key] !== null && activeFilters[col.key] !== undefined;
      var headerContent = [col.title];

      if (col.filters) {
        headerContent.push(
          React.createElement(Dropdown, {
            key: 'filter-' + col.key,
            trigger: ['click'],
            overlay: React.createElement(Menu, {
              onClick: function(e) {
                var next = Object.assign({}, activeFilters);
                if (String(e.key) === String(activeFilters[col.key])) {
                  delete next[col.key];
                } else {
                  next[col.key] = e.key;
                }
                setActiveFilters(next);
              }
            },
              col.filters.map(function(f) {
                return React.createElement(Menu.Item, {
                  key: f.value,
                  style: String(activeFilters[col.key]) === String(f.value) ? { background: '#e6f7ff', fontWeight: 500 } : {}
                }, f.label);
              })
            )
          },
            React.createElement(Icon, {
              type: 'filter',
              theme: filterActive ? 'filled' : 'outlined',
              style: { marginLeft: 6, fontSize: 11, color: filterActive ? '#1890ff' : '#bfbfbf', cursor: 'pointer' }
            })
          )
        );
        if (filterActive) {
          headerContent.push(
            React.createElement(Tooltip, { key: 'clear-' + col.key, title: 'Limpar filtro' },
              React.createElement(Icon, {
                type: 'close-circle', theme: 'filled',
                style: { marginLeft: 4, fontSize: 11, color: '#ff4d4f', cursor: 'pointer' },
                onClick: function(e) {
                  e.stopPropagation();
                  var next = Object.assign({}, activeFilters);
                  delete next[col.key];
                  setActiveFilters(next);
                }
              })
            )
          );
        }
      }

      ths.push(
        React.createElement('th', {
          key: col.key,
          style: thS(col.width || null)
        }, headerContent)
      );
    });
    if (inlineEdit && onInlineDelete) {
      ths.push(React.createElement('th', { key: '_del', style: thS(50) }, ''));
    }
    return React.createElement('tr', null, ths);
  }, [columns, allSelected, selectedCount, activeFilters, inlineEdit]);

  var itemContent = React.useCallback(function(index) {
    var d = dataRef.current;
    var record = d.data[index];
    if (!record) return null;
    var isSelected = d.selectedMap[record.id];

    var tds = [];
    // Checkbox cell
    tds.push(
      React.createElement('td', {
        key: '_check',
        style: tdS(checkWidth, { textAlign: 'center', position: 'sticky', left: 0, zIndex: 1, overflow: 'visible', background: isSelected ? '#e6f7ff' : '#fff' }),
        onClick: function(e) { e.stopPropagation(); }
      },
        React.createElement(Checkbox, {
          checked: !!isSelected,
          onChange: function(e) { toggleSelect(record.id, e); }
        })
      )
    );

    var isEditingRow = editingId === record.id;

    columns.forEach(function(col, colIdx) {
      var content = col.render ? col.render(record) : record[col.key];
      var isFirstTextCol = colIdx === 0 && inlineEdit;

      if (isFirstTextCol && isEditingRow) {
        // Inline edit input
        tds.push(
          React.createElement('td', {
            key: col.key,
            style: tdS(col.width || null, { background: isSelected ? '#e6f7ff' : '#fff' }),
            onClick: function(e) { e.stopPropagation(); }
          },
            React.createElement(Input, {
              autoFocus: true,
              size: 'small',
              defaultValue: record[col.key] || (col.render ? '' : content),
              onPressEnter: function(e) {
                if (onInlineEdit) onInlineEdit(record, e.target.value);
                setEditingId(null);
              },
              onBlur: function(e) {
                if (onInlineEdit) onInlineEdit(record, e.target.value);
                setEditingId(null);
              }
            })
          )
        );
      } else if (isFirstTextCol) {
        // Clickable text to start edit
        tds.push(
          React.createElement('td', {
            key: col.key,
            style: tdS(col.width || null, isSelected ? { background: '#e6f7ff' } : null),
            onClick: function(e) {
              e.stopPropagation();
              setEditingId(record.id);
              setEditValue(record[col.key] || '');
            }
          },
            React.createElement('span', { style: { cursor: 'pointer' } },
              content,
              React.createElement(Icon, { type: 'edit', style: { marginLeft: 8, fontSize: 12, color: '#bfbfbf', opacity: 0 } })
            )
          )
        );
      } else {
        tds.push(
          React.createElement('td', {
            key: col.key,
            style: tdS(col.width || null, isSelected ? { background: '#e6f7ff' } : null)
          }, content)
        );
      }
    });

    // Inline delete icon
    if (inlineEdit && onInlineDelete && !isEditingRow) {
      tds.push(
        React.createElement('td', {
          key: '_inline_actions',
          style: tdS(50, {
            textAlign: 'center',
            background: isSelected ? '#e6f7ff' : '#fff',
          }),
          onClick: function(e) { e.stopPropagation(); }
        },
          React.createElement(Popconfirm, {
            title: 'Excluir este item?',
            okText: 'Sim',
            cancelText: 'Não',
            zIndex: 1020,
            onConfirm: function() { onInlineDelete(record); }
          },
            React.createElement(Icon, {
              type: 'delete',
              style: { cursor: 'pointer', color: '#ff4d4f', fontSize: 14 }
            })
          )
        )
      );
    }

    return React.createElement(React.Fragment, null, tds);
  }, [columns, onEdit, onDelete, deletingId, editingId]);

  var rowHeight = 42;
  var headerHeight = 42;
  var maxHeight = props.height || (window.innerHeight - 160);
  var contentHeight = headerHeight + (filteredData.length * rowHeight) + (onQuickAdd ? rowHeight : 0);
  var tableHeight = Math.min(contentHeight, maxHeight);

  if (data.length === 0) {
    return React.createElement('div', null,
      headerActions && React.createElement('div', {
        style: { display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }
      }, headerActions),
      React.createElement('div', {
        style: {
          display: 'flex', flexDirection: 'column', alignItems: 'center',
          justifyContent: 'center', height: 200, color: '#bfbfbf'
        }
      },
        React.createElement(Icon, { type: 'inbox', style: { fontSize: 40, marginBottom: 12 } }),
        React.createElement('div', { style: { fontSize: 13 } }, 'Nenhum registro encontrado')
      )
    );
  }

  var fixedFooterContent = onQuickAdd ? function() {
    return React.createElement('tr', null,
      React.createElement('td', {
        colSpan: columns.length + (inlineEdit && onInlineDelete ? 2 : 1),
        style: { padding: '8px 12px', background: '#fafafa', borderTop: '1px solid #e8e8e8' }
      },
        React.createElement('div', {
          style: { display: 'flex', alignItems: 'center', gap: 8 }
        },
          React.createElement(Icon, { type: 'plus', style: { color: '#1890ff', fontSize: 13 } }),
          React.createElement(Input, {
            className: 'settings-quick-add',
            placeholder: 'Adicionar novo...',
            style: { border: 'none', background: 'transparent', boxShadow: 'none', padding: 0, height: 28, fontSize: 13 },
            onPressEnter: function(e) {
              var val = e.target.value && e.target.value.trim();
              if (!val) return;
              onQuickAdd(val);
              e.target.value = '';
            }
          })
        )
      )
    );
  } : undefined;

  return React.createElement('div', { style: { position: 'relative' } },
    // Table
    React.createElement(TableVirtuoso, {
      style: { height: tableHeight, border: '1px solid #e8e8e8', borderRadius: 4 },
      data: filteredData,
      components: tableComponents,
      fixedHeaderContent: fixedHeaderContent,
      fixedFooterContent: fixedFooterContent,
      itemContent: itemContent,
      overscan: 20
    }),
    // Bottom sticky toolbar (when items selected)
    selectedCount > 0 && React.createElement('div', {
      style: {
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
        padding: '10px 20px',
        background: '#262626',
        borderRadius: 8,
        boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
        zIndex: 1015,
        color: '#fff'
      }
    },
      React.createElement(Checkbox, {
        checked: allSelected,
        indeterminate: selectedCount > 0 && !allSelected,
        onChange: toggleAll,
        style: { marginRight: 4 }
      }),
      React.createElement('span', { style: { fontSize: 13, fontWeight: 500 } },
        selectedCount + ' selecionado' + (selectedCount > 1 ? 's' : '')
      ),
      React.createElement('div', { style: { width: 1, height: 20, background: 'rgba(255,255,255,0.2)' } }),
      React.createElement(Button, {
        type: 'danger', size: 'small', icon: 'delete',
        onClick: handleBulkDelete
      }, 'Excluir'),
      React.createElement('a', {
        style: { fontSize: 12, color: 'rgba(255,255,255,0.6)', cursor: 'pointer', marginLeft: 4 },
        onClick: function() { setSelectedMap({}); }
      }, 'Limpar seleção')
    ),
    // Add modal
    onQuickAdd && React.createElement(Modal, {
      title: addLabel,
      visible: addModalVisible,
      onCancel: function() { setAddModalVisible(false); },
      onOk: handleModalAdd,
      okText: 'Adicionar',
      cancelText: 'Cancelar',
      zIndex: 1030,
      destroyOnClose: true,
    },
      React.createElement(Input, {
        placeholder: 'Digite o nome...',
        value: addInputValue,
        onChange: function(e) { setAddInputValue(e.target.value); },
        onPressEnter: handleModalAdd,
        autoFocus: true,
        style: { height: 40, fontSize: 14, borderRadius: 6 }
      })
    )
  );
});

export default SettingsVirtualTable;
